/**
 * Authentication service for Pixiv Downloader
 * Uses backend API with JWT tokens
 */

// Use Cloudflare Pages Functions for auth, external backend for downloads
const AUTH_BASE_URL = window.location.origin; // Same domain as frontend
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

class AuthService {
  constructor() {
    this.token = localStorage.getItem('auth_token');
    this.user = JSON.parse(localStorage.getItem('user') || 'null');
  }

  // Get auth headers
  getAuthHeaders() {
    return this.token ? {
      'Authorization': `Bearer ${this.token}`,
      'Content-Type': 'application/json'
    } : {
      'Content-Type': 'application/json'
    };
  }

  // Sign up new user
  async signUp(email, password, username) {
    try {
      const response = await fetch(`${AUTH_BASE_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, username })
      });

      // Always read the response body once
      let data;
      let responseText;
      try {
        responseText = await response.text();
        data = JSON.parse(responseText);
      } catch (parseError) {
        throw new Error('Invalid response from server');
      }

      if (!response.ok) {
        const errorMessage = data.detail || data.message || 'Registration failed';
        throw new Error(errorMessage);
      }
      
      // Store token and user
      this.token = data.access_token;
      this.user = data.user;
      localStorage.setItem('auth_token', this.token);
      localStorage.setItem('user', JSON.stringify(this.user));

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }

  // Sign in user
  async signIn(email, password) {
    try {
      const response = await fetch(`${AUTH_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      // Always read the response body once
      let data;
      let responseText;
      try {
        responseText = await response.text();
        data = JSON.parse(responseText);
      } catch (parseError) {
        throw new Error('Invalid response from server');
      }

      if (!response.ok) {
        let errorMessage = data.detail || data.message || 'Login failed';
        
        // Provide more user-friendly error messages
        if (response.status === 401) {
          errorMessage = 'Invalid email or password. Please check your credentials.';
        }
        
        throw new Error(errorMessage);
      }
      
      // Store token and user
      this.token = data.access_token;
      this.user = data.user;
      localStorage.setItem('auth_token', this.token);
      localStorage.setItem('user', JSON.stringify(this.user));

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }

  // Sign out user
  async signOut() {
    this.token = null;
    this.user = null;
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
  }

  // Get current user
  async getCurrentUser() {
    if (!this.token) {
      return { user: null, profile: null };
    }

    try {
      const response = await fetch(`${AUTH_BASE_URL}/api/auth/me`, {
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        // Token might be expired
        await this.signOut();
        return { user: null, profile: null };
      }

      // Always read the response body once
      let user;
      try {
        const responseText = await response.text();
        user = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Failed to parse user response:', parseError);
        await this.signOut();
        return { user: null, profile: null };
      }

      this.user = user;
      localStorage.setItem('user', JSON.stringify(user));

      return { user, profile: user };
    } catch (error) {
      console.error('Failed to get current user:', error);
      await this.signOut();
      return { user: null, profile: null };
    }
  }

  // Update user profile
  async updateProfile(updates) {
    try {
      const response = await fetch(`${API_BASE_URL}/users/settings`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ settings: updates })
      });

      // Always read the response body once
      let data;
      let responseText;
      try {
        responseText = await response.text();
        data = JSON.parse(responseText);
      } catch (parseError) {
        throw new Error('Invalid response from server');
      }

      if (!response.ok) {
        const errorMessage = data.detail || data.message || 'Update failed';
        throw new Error(errorMessage);
      }

      this.user = data;
      localStorage.setItem('user', JSON.stringify(data));

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }

  // Check if user is authenticated
  isAuthenticated() {
    return !!(this.token && this.user);
  }

  // Get current user (synchronous)
  getUser() {
    return this.user;
  }

  // Listen to auth changes (for compatibility with existing code)
  onAuthStateChange(callback) {
    // Simple implementation - call callback immediately with current state
    callback({ data: { user: this.user } });
    
    // Return unsubscribe function
    return () => {};
  }

  // Make authenticated API request (for compatibility with existing Dashboard)
  async apiRequest(endpoint, options = {}) {
    const token = this.token;
    
    if (!token) {
      console.error('No authentication token found');
      return { success: false, error: 'No authentication token' };
    }

    const config = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    };

    try {
      console.log(`Making API request to: ${API_BASE_URL}${endpoint}`);
      const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
      
      // Always read the response body once
      let responseText;
      let data;
      try {
        responseText = await response.text();
        data = responseText ? JSON.parse(responseText) : {};
      } catch (parseError) {
        console.error('Failed to parse API response:', parseError);
        return { success: false, error: 'Invalid response format from server' };
      }
      
      if (response.status === 401) {
        console.error('Authentication failed - token expired or invalid');
        await this.signOut();
        return { success: false, error: 'Authentication failed' };
      }

      if (!response.ok) {
        const errorMessage = data.detail || data.message || `API request failed: ${response.status}`;
        return { 
          success: false, 
          error: errorMessage,
          status: response.status
        };
      }

      return { success: true, data };

    } catch (error) {
      console.error('API request error:', error);
      
      if (error.message.includes('Failed to fetch') || 
          error.message.includes('NetworkError') ||
          error.name === 'TypeError') {
        return { 
          success: false, 
          error: 'Cannot connect to backend server',
          details: `Trying to connect to: ${API_BASE_URL}${endpoint}`
        };
      }
      
      return { success: false, error: error.message };
    }
  }
}

// Create singleton instance
const authService = new AuthService();

// Export auth helpers for compatibility
export const authHelpers = {
  signUp: (email, password, username) => authService.signUp(email, password, username),
  signIn: (email, password) => authService.signIn(email, password),
  signOut: () => authService.signOut(),
  getCurrentUser: () => authService.getCurrentUser(),
  updateProfile: (updates) => authService.updateProfile(updates),
  onAuthStateChange: (callback) => authService.onAuthStateChange(callback)
};

// Export service instance
export default authService;

// Export helper function
export const isAuthConfigured = () => true; // Always true for backend auth